type AnalyticsOptions = {
  measurementId?: string
  environment: 'development' | 'staging' | 'production'
  appUrl: string
}

type PageViewTracker = (href: string) => void
type GoogleTag = (command: string, ...parameters: Array<unknown>) => void

declare global {
  interface Window {
    dataLayer?: Array<IArguments>
    gtag?: GoogleTag
  }
}

const campaignParameters = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_id',
  'utm_content',
  'utm_term',
]

let pageViewTracker: PageViewTracker | undefined

function trackingUrl(href: string) {
  const url = new URL(href, window.location.origin)
  const result = new URL(url.pathname, url.origin)
  result.pathname = result.pathname.replace(/\/+$/, '') || '/'

  // Keep campaign attribution without sending authentication or form queries.
  for (const parameter of campaignParameters) {
    const value = url.searchParams.get(parameter)
    if (value) result.searchParams.set(parameter, value)
  }

  return result
}

export function initializeAnalytics({
  measurementId,
  environment,
  appUrl,
}: AnalyticsOptions): PageViewTracker | undefined {
  if (
    !measurementId ||
    !/^G-[A-Z0-9]+$/.test(measurementId) ||
    environment === 'development' ||
    window.location.origin !== new URL(appUrl).origin
  ) {
    return
  }

  if (pageViewTracker) return pageViewTracker

  const entryUrl = trackingUrl(window.location.href)
  const dataLayer = (window.dataLayer ??= [])
  const gtag: GoogleTag = (window.gtag ??= function () {
    // Preserve the arguments object expected by the Google tag queue.
    dataLayer.push(arguments)
  })

  let lastPath: string | undefined
  let referrer = document.referrer
    ? new URL(new URL(document.referrer).pathname, document.referrer).href
    : ''

  gtag('js', new Date())
  gtag('config', measurementId, {
    send_page_view: false,
    page_location: entryUrl.href,
    page_referrer: referrer,
    cookie_domain: window.location.hostname,
    ...(environment === 'staging' ? { debug_mode: true } : {}),
  })

  const script = document.createElement('script')
  script.async = true
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`
  document.head.appendChild(script)

  pageViewTracker = (href) => {
    const pageUrl = trackingUrl(href)
    if (
      pageUrl.origin !== window.location.origin ||
      pageUrl.pathname === lastPath
    ) {
      return
    }

    // The router can remove campaign queries while resolving the landing page.
    if (lastPath === undefined) {
      for (const parameter of campaignParameters) {
        const value = entryUrl.searchParams.get(parameter)
        if (value && !pageUrl.searchParams.has(parameter)) {
          pageUrl.searchParams.set(parameter, value)
        }
      }
    }

    gtag('event', 'page_view', {
      send_to: measurementId,
      page_location: pageUrl.href,
      page_path: pageUrl.pathname,
      page_title: document.title,
      page_referrer: referrer,
    })

    lastPath = pageUrl.pathname
    referrer = new URL(pageUrl.pathname, pageUrl.origin).href
  }

  return pageViewTracker
}
